import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { lazy, Suspense } from 'react';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Eager load: auth + landing (first things users see)
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { LandingPage } from './pages/LandingPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { DashboardPage } from './pages/DashboardPage';

// Lazy load: everything else (loaded on demand)
const AllDealsPage = lazy(() => import('./pages/AllDealsPage'));
const StageViewPage = lazy(() => import('./pages/StageViewPage'));
const AccountDetailPage = lazy(() => import('./pages/AccountDetailPage'));
const PartnersPage = lazy(() => import('./pages/PartnersPage'));
const QuickNotesPage = lazy(() => import('./pages/QuickNotesPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const TodosPage = lazy(() => import('./pages/TodosPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const CommissionsPage = lazy(() => import('./pages/CommissionsPage'));
const RemindersPage = lazy(() => import('./pages/RemindersPage'));
const ReminderManagerPage = lazy(() => import('./pages/ReminderManagerPage'));
const DealTemplatePage = lazy(() => import('./pages/DealTemplatePage'));
const MeetingFormsPage = lazy(() => import('./pages/MeetingFormsPage'));
const RevisitPage = lazy(() => import('./pages/RevisitPage'));
const TasksPage = lazy(() => import('./pages/TasksPage'));
const CreatorDashboardPage = lazy(() => import('./pages/CreatorDashboardPage'));
const ManagerFormsPage = lazy(() => import('./pages/ManagerFormsPage'));
const ChangePasswordPage = lazy(() => import('./pages/ChangePasswordPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const BillingSuccessPage = lazy(() => import('./pages/BillingSuccessPage'));

const PageLoader = () => (
  <div className="flex h-screen items-center justify-center bg-slate-50">
    <div className="w-8 h-8 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin" />
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/billing" element={<ProtectedRoute allowForcedChange><BillingPage /></ProtectedRoute>} />
            <Route path="/billing/success" element={<ProtectedRoute allowForcedChange><BillingSuccessPage /></ProtectedRoute>} />
            <Route path="/change-password" element={<ProtectedRoute allowForcedChange><ChangePasswordPage /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/all-deals" element={<ProtectedRoute><AllDealsPage /></ProtectedRoute>} />
            <Route path="/pipeline/stage/:stageId" element={<ProtectedRoute><StageViewPage /></ProtectedRoute>} />
            <Route path="/account/:accountId" element={<ProtectedRoute><AccountDetailPage /></ProtectedRoute>} />
            <Route path="/partners" element={<ProtectedRoute><PartnersPage /></ProtectedRoute>} />
            <Route path="/quick-notes" element={<ProtectedRoute><QuickNotesPage /></ProtectedRoute>} />
            <Route path="/todos" element={<Navigate to="/tasks?tab=boards" replace />} />
            <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
            <Route path="/commissions" element={<ProtectedRoute><CommissionsPage /></ProtectedRoute>} />
            <Route path="/reminders" element={<Navigate to="/tasks" replace />} />
            <Route path="/reminders/manager" element={<ProtectedRoute><ReminderManagerPage /></ProtectedRoute>} />
            <Route path="/reminders/legacy" element={<ProtectedRoute><RemindersPage /></ProtectedRoute>} />
            <Route path="/todos/legacy" element={<ProtectedRoute><TodosPage /></ProtectedRoute>} />
            <Route path="/tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/settings/deal-template" element={<ProtectedRoute><DealTemplatePage /></ProtectedRoute>} />
            <Route path="/settings/meeting-forms" element={<ProtectedRoute><MeetingFormsPage /></ProtectedRoute>} />
            <Route path="/revisit" element={<ProtectedRoute><RevisitPage /></ProtectedRoute>} />
            <Route path="/mockups/tasks" element={<Navigate to="/tasks" replace />} />
            <Route path="/mockups/dashboard" element={<Navigate to="/dashboard" replace />} />
            <Route path="/creator" element={<ProtectedRoute><CreatorDashboardPage /></ProtectedRoute>} />
            <Route path="/forms" element={<ProtectedRoute><ManagerFormsPage /></ProtectedRoute>} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" />
    </AuthProvider>
  );
}

export default App;
